import styled, { css } from 'styled-components';

import { Cross } from '@solent-university/solent-icons';
import DefaultButton from 'Elements/Button';
import scrollbarStyle from 'Common/styles/scrollbar';
import IconElement from 'Elements/Icon';

//Table
export const Container = styled.div`
    ${({ theme }) => css`
        ${scrollbarStyle('secondary')}
        display: flex;
        position: relative;
        padding: 1.75rem 0 0 0.5rem;
        border-radius: 0.875rem 0.875rem 0 0;
        overflow: auto;
        align-items: flex-start;
        background-color: ${theme.colors.primaryContrast.hex};
        max-height: calc(85vh);

        @media ${theme.breakpoints.medium.media} {
            max-height: calc(100vh - 1.5rem);
            padding: 2rem 0 0 1.5rem;
        }
    `}
`;

export const DataColumns = styled.div<{ $isScrolling: boolean }>`
    ${({ theme, $isScrolling }) => css`
        ${scrollbarStyle('secondary')}
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        column-gap: 0.5rem;
        overflow-x: auto;
        padding: 1rem 0.5rem 0 0.25rem;

        /* stylelint-disable-next-line */
        @media only screen and (max-width: ${theme.breakpoints.smallLarge.value + 8}px) {
            ${$isScrolling &&
            css`
                padding-left: 1.5rem;
            `}
        }

        @media ${theme.breakpoints.medium.media} {
            column-gap: 2rem;
            padding: 1rem 2rem 0 1rem;
        }
    `}
`;

export const Column = styled.div`
    ${({ theme }) => css`
        width: 9rem;
        display: flex;
        flex-direction: column;
        background: ${theme.colors.primaryContrast.hex};
        position: relative;
        border-radius: 0.5rem;
        height: auto;

        @media ${theme.breakpoints.medium.media} {
            width: 19.5rem;
        }
    `}
`;

export const ColumnSlot = styled.div<{ isEmpty: boolean }>`
    ${({ theme, isEmpty }) => css`
        position: relative;
        width: 9rem;
        min-height: 10rem;
        height: auto;
        border: ${isEmpty ? `0.1rem dashed ${theme.colors.secondary.tints[2].hex}` : 'none'};
        border-radius: 0.5rem;
        background-color: ${isEmpty ? 'transparent' : theme.colors.background.hex};
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0.5rem;
        height: 99%;

        @media ${theme.breakpoints.medium.media} {
            padding: 0 2.5rem;
            width: 19.5rem;
        }
    `}
`;

export const Cell = styled.span`
    display: inline-flex;
    justify-content: center;
    align-items: center;
    height: 3.75rem;
    text-align: center;
    position: relative;
    width: 100%;
    padding: 0 0.5rem;
    line-height: 1.2;
    font-size: 1rem;

    &:not(:last-child) {
        border-bottom: 0.0625rem solid rgba(0, 0, 0, 0.15);
    }
`;

export const Heading = styled(Cell)<{ $hidden?: boolean }>`
    ${({ $hidden }) => css`
        font-weight: 400;
        justify-content: flex-start;
        text-align: left;
        padding-right: 1rem;

        ${$hidden &&
        css`
            display: none;
        `}
    `}
`;

export const HeadingText = styled.span`
    ${({ theme }) => css`
        font-size: 1rem;
        opacity: 1;
        line-height: 1.4;
        font-weight: 300;
        transition: opacity 25ms ease 200ms, width 200ms ease;
        font-family: ${theme.fonts.secondary};
    `}
`;

export const RemoveTextButton = styled.button`
    ${({ theme }) => css`
        border: none;
        background: none;
        text-decoration: underline;
        padding: 0 0.5rem;
        display: flex;
        justify-content: center;
        width: 100%;
        margin: 0.75rem 0 1.5rem;
        cursor: pointer;
        font-size: 1.125rem;
        color: ${theme.colors.primary.hex};
        font-size: 1rem;

        ${theme.breakpoints.medium.media} {
            margin: 0.75rem 0 1.5rem;
            font-size: 1.25rem;
        }

        &:hover {
            text-decoration: none;
        }
    `}
`;

export const RemoveAllTextButton = styled(RemoveTextButton)<{ $isScrolling: boolean }>`
    ${({ $isScrolling, theme }) => css`
        display: block;

        /* stylelint-disable-next-line */
        @media only screen and (max-width: ${theme.breakpoints.smallLarge.value + 8}px) {
            ${$isScrolling &&
            css`
                display: none;
            `}
        }
    `}
`;

export const HeadingsColumn = styled(Column)<{ $isScrolling: boolean }>`
    ${({ theme, $isScrolling }) => {
        const opacityDelay = $isScrolling ? '50ms' : '300ms';

        return css`
            width: 11rem;
            margin-top: 10.1rem;
            margin-right: 0.25rem;
            flex: 0 0 auto;
            transition: transform 100ms ease, width 100ms ease, opacity ${opacityDelay} ease-in-out;
            transform: translateX(0);
            position: relative;
            z-index: 5;

            /* Hide heading text on mobile when scrolling */

            /* stylelint-disable-next-line */
            @media only screen and (max-width: ${theme.breakpoints.smallLarge.value + 8}px) {
                opacity: 1;

                ${$isScrolling &&
                css`
                    ${Heading} {
                        transform: translateX(-100%);
                    }
                    opacity: 0;
                    width: 0;
                    pointer-events: none;
                `}
            }

            @media ${theme.breakpoints.medium.media} {
                width: 18rem;
                margin-top: 19.25rem;
                margin-right: 1rem;
            }
        `;
    }}
`;

export const IconWrapper = styled.span`
    ${({ theme }) => css`
        display: inline-flex;
        justify-content: center;
        align-items: center;
        display: none;

        @media ${theme.breakpoints.medium.media} {
            display: block;
        }
    `}
`;

export const Icon = styled.svg`
    ${({ theme }) => css`
        color: ${theme.colors.tertiary.hex};
        width: 1.25rem;
        height: 1.25rem;
        margin: 0 1rem;

        @media ${theme.breakpoints.medium.media} {
            margin: 0 0.75rem;
            width: 2rem;
            height: 2rem;
        }
    `}
`;

export const CourseName = styled(Cell)`
    ${({ theme }) => css`
        z-index: 1;
        border-radius: 0.5rem;
        display: flex;
        flex-direction: column;
        text-align: left;
        justify-content: flex-start;
        line-height: 1.2;
        font-size: 1rem;
        font-weight: 600;
        height: 9.1rem;
        align-items: flex-start;
        padding-left: 0;

        span {
            display: block;
            font-weight: 200;
            margin-top: 0.5rem;
            text-align: left;
            align-self: flex-start;
            font-family: ${theme.fonts.secondary};
        }

        @media ${theme.breakpoints.medium.media} {
            color: ${theme.colors.text.hex};
            font-size: 1.25rem;
            margin-bottom: 0.75rem;
            height: 7.5rem;
        }

        &:not(:last-child) {
            border: none;
        }

        &:before {
            content: none;
        }
    `}
`;

export const CourseImageWrapper = styled.div`
    border-radius: inherit;
    overflow: hidden;
    display: none;
    height: 7.5rem;

    @media ${({ theme }) => theme.breakpoints.medium.media} {
        height: 10rem;
        width: 100%;
        display: block;
        max-width: 15.625rem;
    }
`;

export const RemoveButton = styled.button`
    ${({ theme }) => css`
        position: fixed;
        right: 0.75rem;
        top: 0.5rem;
        background-color: ${theme.colors.primaryContrast.hex};
        border: 0.1rem solid ${theme.colors.secondary.tints[3].hex};
        border-radius: 0.4rem;
        width: 2rem;
        height: 2rem;
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 0;
        cursor: pointer;
        z-index: 2;

        &:hover {
            svg {
                stroke-width: 0.125rem;
            }
        }

        @media ${theme.breakpoints.xlarge.media} {
            right: 0.5rem;
        }
    `}
`;

export const RemoveIcon = styled(Cross)`
    width: 1rem;
    height: 1rem;
`;

export const CourseLevel = styled(Cell)`
    ${({ theme }) => css`
        font-weight: 400;

        @media ${theme.breakpoints.medium.media} {
            font-size: 1rem;
        }
    `}
`;

export const Button = styled(DefaultButton)`
    padding: 0.5rem;
    justify-content: center;
    font-size: 0.875rem;
    display: flex;
    width: 100%;
    max-width: 14.375rem;
    margin: 1rem auto 0;

    @media ${({ theme }) => theme.breakpoints.medium.media} {
        font-size: 1rem;
        padding: 0.625rem 1.5rem;
    }

    @media ${({ theme }) => theme.breakpoints.large.media} {
        font-size: 1.25rem;
    }
`;

export const ColumnSlotContainer = styled.div`
    ${({ theme }) => css`
        display: flex;
        flex-direction: column;

        p {
            font-weight: bold;
            color: ${theme.colors.accents[5].hex};
            text-align: center;
            margin-bottom: 0.25rem;
            line-height: 1.2;
            font-size: 1rem;

            @media ${theme.breakpoints.medium.media} {
                font-size: 1.25rem;
            }
        }
    `}
`;

export const TooltipWrapper = styled.div`
    position: absolute;
    right: 0;
    align-self: center;
    z-index: 1;
`;

export const TooltipIcon = styled(IconElement)`
    ${({ theme }) => css`
        color: ${theme.colors.primaryContrast.hex};
        background: ${theme.colors.accents[5].hex};
        height: 1.25rem;
        width: 1.25rem;
        border-radius: 50%;

        svg {
            width: 0.75rem;
            height: 0.75rem;
        }
    `}
`;
